import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Client } from '@pf/shared';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch, apiDownload } from '../lib/api';

export interface ClientStats {
  messages_sent: number;
  replies: number;
  reply_rate: number;
  coverage_count: number;
}

export interface ActivityEntry {
  id: string;
  type: 'message_sent' | 'contact_added';
  timestamp: string;
  subject?: string | null;
  toEmail?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  pressListName?: string | null;
}

export interface ActivityResponse {
  activity: ActivityEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface ClientsParams {
  page?: number;
  limit?: number;
  search?: string;
  archived?: boolean;
}

export interface CreateClientBody {
  name: string;
  description?: string | null;
  contactEmails?: string[];
  logoUrl?: string | null;
  website?: string | null;
  notes?: string | null;
}

export type UpdateClientBody = Partial<CreateClientBody>;

const CLIENTS_KEY = 'clients';

export function useClients(params: ClientsParams = {}) {
  return useQuery({
    queryKey: [CLIENTS_KEY, params],
    queryFn: () =>
      apiGet<{ clients: Client[] }>('/clients', {
        page: params.page ?? 1,
        limit: params.limit ?? 100,
        search: params.search,
        archived: params.archived,
      }),
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: [CLIENTS_KEY, id],
    queryFn: () => apiGet<{ client: Client }>(`/clients/${id}`),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateClientBody) =>
      apiPost<{ client: Client }>('/clients', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateClientBody }) =>
      apiPut<{ client: Client }>(`/clients/${id}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY, variables.id] });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/clients/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
    },
  });
}

export function useArchiveClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ client: Client }>(`/clients/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
    },
  });
}

export function useUnarchiveClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ client: Client }>(`/clients/${id}/unarchive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
    },
  });
}

export function useClientStats(id: string | undefined) {
  return useQuery({
    queryKey: [CLIENTS_KEY, id, 'stats'],
    queryFn: () => apiGet<ClientStats>(`/clients/${id}/stats`),
    enabled: !!id,
  });
}

export function useClientActivity(id: string | undefined, params: { page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: [CLIENTS_KEY, id, 'activity', params],
    queryFn: () =>
      apiGet<ActivityResponse>(`/clients/${id}/activity`, {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
      }),
    enabled: !!id,
  });
}

export interface ClientMessage {
  id: string;
  clientId: string;
  userId: string;
  emailMessageId: string;
  sender: string;
  recipient: string;
  subject: string | null;
  bodySnippet: string | null;
  messageType: string;
  readAt: string | null;
  createdAt: string;
}

export interface ClientCommunicationsResponse {
  messages: ClientMessage[];
  total: number;
  page: number;
  limit: number;
}

export function useClientCommunications(
  id: string | undefined,
  params: { page?: number; limit?: number } = {},
) {
  return useQuery({
    queryKey: [CLIENTS_KEY, id, 'communications', params],
    queryFn: () =>
      apiGet<ClientCommunicationsResponse>(`/clients/${id}/communications`, {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
      }),
    enabled: !!id,
  });
}

export function useMarkClientMessageRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, messageId }: { clientId: string; messageId: string }) =>
      apiPatch<{ message: ClientMessage }>(`/clients/${clientId}/communications/${messageId}/read`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY, variables.clientId, 'communications'] });
    },
  });
}

export interface ClientReportHitsByMonth {
  month: string;
  count: number;
}

export interface ClientReportTopPublication {
  publicationName: string;
  count: number;
}

export interface ClientReportTopReporter {
  contactId: string;
  firstName: string;
  lastName: string;
  count: number;
}

export interface ClientReport {
  total_hits: number;
  hits_by_month: ClientReportHitsByMonth[];
  top_publications: ClientReportTopPublication[];
  top_reporters: ClientReportTopReporter[];
  outreach_summary: {
    messages_sent: number;
    replies: number;
    reply_rate: number;
  };
}

export function useClientReport(id: string | undefined) {
  return useQuery({
    queryKey: [CLIENTS_KEY, id, 'report'],
    queryFn: () => apiGet<ClientReport>(`/clients/${id}/report`),
    enabled: !!id,
  });
}

export function useDownloadClientReport() {
  return useMutation({
    mutationFn: ({ clientId, clientName }: { clientId: string; clientName: string }) =>
      apiDownload(
        `/clients/${clientId}/report/export`,
        undefined,
        `${clientName.replace(/[^a-z0-9_-]/gi, '_').slice(0, 50)}_report.csv`,
      ),
  });
}
